import React, { useCallback, useEffect, useState } from "react"
import * as Icons from "lucide-react"
import { useEmailState } from "./hooks/useEmailState"
import EmailSidebar from "./components/EmailSidebar"
import EmailList from "./components/EmailList"
import EmailViewer from "./components/EmailViewer"
import EmailCompose from "./components/EmailCompose"
import EmailSearch from "./components/EmailSearch"
import EmailContextMenu from "./components/EmailContextMenu"
import EmailRibbon from "./components/EmailRibbon"
import EmailNavRail from "./components/EmailNavRail"
import EmailCalendar from "./components/EmailCalendar"
import EmailPeople from "./components/EmailPeople"
import EmailTasks from "./components/EmailTasks"
import EmailAccountSettings from "./components/EmailAccountSettings"
import EmailNewMailToast from "./components/EmailNewMailToast"
import EmailRules from "./components/EmailRules"
import EmailReadAloud from "./components/EmailReadAloud"
import EmailMeeting from "./components/EmailMeeting"
import EmailReminders from "./components/EmailReminders"
import EmailFolderModal from "./components/EmailFolderModal"
import emailApi from "./services/emailApi"

export default function EmailModule() {
  const state = useEmailState()
  const [smtpAccounts, setSmtpAccounts] = useState([])
  const [showCompose, setShowCompose] = useState(false)
  const [composeMode, setComposeMode] = useState("new")
  const [composeInitial, setComposeInitial] = useState(null)
  const [activeView, setActiveView] = useState("mail")
  const [showAccountSettings, setShowAccountSettings] = useState(false)
  const [addingMailbox, setAddingMailbox] = useState(false)
  const [showRules, setShowRules] = useState(false)

  // ── View / layout UI state driven by the ribbon ──
  const [folderPaneOpen, setFolderPaneOpen] = useState(true)
  const [ribbonCollapsed, setRibbonCollapsed] = useState(false)
  const [density, setDensity] = useState("normal") // 'normal' | 'compact'
  const [messagePreview, setMessagePreview] = useState(true)
  const [showConversations, setShowConversations] = useState(false)
  const [sortBy, setSortBy] = useState("date") // 'date' | 'from' | 'flag'
  const [sortDir, setSortDir] = useState("desc") // 'desc' | 'asc'
  const [workOffline, setWorkOffline] = useState(false)
  const [readAloudText, setReadAloudText] = useState(null)
  const [showMeeting, setShowMeeting] = useState(false)
  const [showReminders, setShowReminders] = useState(false)
  const [folderModal, setFolderModal] = useState(null)
  const [folderModalSaving, setFolderModalSaving] = useState(false)
  const [folderProperties, setFolderProperties] = useState(null)

  // ── Rules (Outlook-style), persisted locally per account ──
  const rulesKey = `em-rules-${state.activeAccount?.account_id || "default"}`
  const [rules, setRules] = useState([])
  useEffect(() => {
    try {
      setRules(JSON.parse(localStorage.getItem(rulesKey) || "[]"))
    } catch {
      setRules([])
    }
  }, [rulesKey])
  const saveRules = useCallback(
    next => {
      setRules(next)
      try {
        localStorage.setItem(rulesKey, JSON.stringify(next))
      } catch {
        /* ignore */
      }
    },
    [rulesKey]
  )

  const applyRules = useCallback(
    listArg => {
      const list = (Array.isArray(listArg) ? listArg : rules).filter(
        r => r.enabled
      )
      if (!list.length) {
        state.showToast("No active rules to run.", "info")
        return
      }
      let acted = 0
      ;(state.messages || []).forEach(msg => {
        for (const rule of list) {
          const hay = (
            rule.field === "subject"
              ? msg.subject || ""
              : rule.field === "body"
              ? msg.body_text || msg.body_html || ""
              : `${msg.from_address || ""} ${
                  msg.from_display || msg.from_name || ""
                }`
          ).toLowerCase()
          if (rule.value && hay.includes(rule.value.toLowerCase())) {
            acted++
            if (rule.action === "flag") state.handleToggleFlag(msg)
            else if (rule.action === "star") state.handleToggleStar(msg)
            else if (rule.action === "archive") state.handleArchive(msg)
            else if (rule.action === "delete") state.handleDelete(msg)
            break // one action per message per run
          }
        }
      })
      state.showToast(
        acted
          ? `Rules ran on ${acted} message${acted === 1 ? "" : "s"}.`
          : "No messages matched your rules.",
        acted ? "success" : "info"
      )
    },
    [rules, state]
  )

  const markAllRead = useCallback(() => {
    if (state.selectedMessages && state.selectedMessages.size > 0)
      state.handleBulkAction("read")
    else state.showToast("Select messages first to mark them as read.", "info")
  }, [state])

  // Auto-dismiss the "new mail" desktop toast after a few seconds.
  useEffect(() => {
    if (!state.newMailNotice) return undefined
    const t = setTimeout(() => state.setNewMailNotice(null), 9000)
    return () => clearTimeout(t)
  }, [state.newMailNotice])

  useEffect(() => {
    const loadSmtp = async () => {
      try {
        const userInfo = JSON.parse(sessionStorage.getItem("user-info") || "{}")
        const details = userInfo?.user_details || userInfo
        const userId = details?.id || details?.user_id
        const res = await emailApi.getSmtpAccounts(
          userId ? { user_id: userId, page: 1, size: 50 } : {}
        )
        const data = Array.isArray(res.data)
          ? res.data
          : res.data?.results || []
        setSmtpAccounts(data)
      } catch (err) {
        console.error("Failed to load SMTP accounts", err)
      }
    }
    loadSmtp()
  }, [])

  const handleReply = useCallback(msg => {
    setComposeMode("reply")
    setComposeInitial({ message: msg, type: "reply" })
    setShowCompose(true)
  }, [])

  const handleReplyAll = useCallback(msg => {
    setComposeMode("reply_all")
    setComposeInitial({ message: msg, type: "reply_all" })
    setShowCompose(true)
  }, [])

  const handleForward = useCallback(msg => {
    setComposeMode("forward")
    setComposeInitial({ message: msg, type: "forward" })
    setShowCompose(true)
  }, [])

  const handleNewMail = useCallback(() => {
    setComposeMode("new")
    setComposeInitial(null)
    setShowCompose(true)
  }, [])

  const handleComposeTo = useCallback((to, toName) => {
    setComposeMode("new")
    setComposeInitial({ to, toName })
    setShowCompose(true)
  }, [])

  const handleComposeClose = useCallback(() => {
    setShowCompose(false)
    setComposeInitial(null)
  }, [])

  const handleComposeSent = useCallback(() => {
    setShowCompose(false)
    setComposeInitial(null)
    state.fetchMessages(state.activeFolder, state.activeAccount?.account_id)
    state.fetchStats(state.activeAccount?.account_id)
  }, [state])

  const handlePrint = useCallback(msg => {
    const printWin = window.open("", "_blank")
    if (!printWin) return
    printWin.document.write(`
      <html><head><title>${msg.subject || "Email"}</title></head><body>
      <h2>${msg.subject || "(no subject)"}</h2>
      <p><strong>From:</strong> ${msg.from_display || msg.from_address}</p>
      <p><strong>To:</strong> ${
        msg.to_display || (msg.to_addresses || []).join(", ")
      }</p>
      <hr/>
      ${msg.body_html || `<pre>${msg.body_text || ""}</pre>`}
      </body></html>
    `)
    printWin.document.close()
    printWin.print()
  }, [])

  const handleDownload = useCallback(msg => {
    const blob = new Blob([msg.body_html || msg.body_text || ""], {
      type: msg.body_html ? "text/html" : "text/plain",
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${msg.subject || "email"}.${msg.body_html ? "html" : "txt"}`
    a.click()
    URL.revokeObjectURL(url)
  }, [])

  // ────────────────────────────────────────────────────────────────────────
  // Ribbon command helpers — every ribbon command maps to a real action here.
  // ────────────────────────────────────────────────────────────────────────
  const info = useCallback(msg => state.showToast(msg, "info"), [state])
  const active = () => state.activeMessage

  const folderIdBySystem = useCallback(
    sys => {
      const f = (state.folders || []).find(x => x.system_folder === sys)
      return f?.folder_id
    },
    [state.folders]
  )

  const activeFolderObj = useCallback(
    () => (state.folders || []).find(f => f.name === state.activeFolder),
    [state.folders, state.activeFolder]
  )

  const refreshFolderData = useCallback(
    async folderName => {
      const accountId = state.activeAccount?.account_id
      if (!accountId) return
      await Promise.all([
        state.fetchFolders(accountId),
        state.fetchStats(accountId),
        state.fetchMessages(folderName || state.activeFolder, accountId),
      ])
    },
    [state]
  )

  const handleFolderContextAction = useCallback(
    async (action, folder, shouldConfirm) => {
      const folderId = folder?.folder_id
      const folderName = folder?.name || state.activeFolder
      const accountId = state.activeAccount?.account_id
      if (shouldConfirm) {
        const prompts = {
          cleanUp: `Clean up the folder "${folder?.label || folderName}"?`,
          deleteAll: `Delete all messages in "${folder?.label || folderName}"?`,
          deleteFolder: `Delete the folder "${folder?.label || folderName}"?`,
        }
        if (!window.confirm(prompts[action])) return
      }

      try {
        if (action === "newFolder") {
          setFolderModal({ mode: "create" })
          return
        }
        if (!folderId && action !== "newFolder") {
          state.showToast("This folder does not have a server folder ID.", "error")
          return
        }
        if (action === "renameFolder") {
          setFolderModal({ mode: "rename", folderId, initialName: folderName })
          return
        }
        if (action === "markAllRead") {
          await emailApi.markAllFolderRead(folderId)
          await refreshFolderData(folderName)
          state.showToast("All messages marked as read.", "success")
        } else if (action === "cleanUp") {
          await emailApi.cleanUpFolder(folderId)
          await refreshFolderData(folderName)
          state.showToast("Folder cleaned up.", "success")
        } else if (action === "deleteAll") {
          await emailApi.deleteAllFolder(folderId)
          await refreshFolderData(folderName)
          state.showToast("All messages deleted.", "success")
        } else if (action === "deleteFolder") {
          await emailApi.deleteFolder(folderId)
          await state.fetchFolders(accountId)
          state.selectFolder("inbox")
          await refreshFolderData("inbox")
          state.showToast("Folder deleted.", "success")
        } else if (action === "properties") {
          const res = await emailApi.getFolderProperties(folderId)
          setFolderProperties({ folder: folder, data: res.data })
        } else if (action === "moveUp" || action === "moveDown") {
          await emailApi.reorderFolders({
            folder_id: folderId,
            direction: action === "moveUp" ? "up" : "down",
          })
          await refreshFolderData(folderName)
          state.showToast("Folder order updated.", "success")
        }
      } catch (error) {
        const detail = error?.response?.data?.detail || error?.response?.data?.error
        state.showToast(detail || "Folder action could not be completed.", "error")
      }
    },
    [state, refreshFolderData]
  )

  const moveActiveTo = useCallback(
    sys => {
      const m = active()
      if (!m) return
      const fid = folderIdBySystem(sys)
      if (fid) {
        state.handleMove(m, fid)
        state.setActiveMessage(null)
      } else
        state.showToast(
          `No ${sys} folder is available for this mailbox.`,
          "info"
        )
    },
    [state, folderIdBySystem]
  )

  const markActiveUnread = useCallback(
    msg => {
      const m = msg || state.activeMessage
      if (!m) return
      const id = m.id || m.message_id
      state.clearReadStateOverride(m)
      emailApi
        .toggleRead(id)
        .then(() => {
          state.fetchMessages(
            state.activeFolder,
            state.activeAccount?.account_id
          )
          state.fetchStats(state.activeAccount?.account_id)
          state.setActiveMessage(null)
          state.showToast("Marked as unread.", "success")
        })
        .catch(() => state.showToast("Couldn't mark as unread.", "error"))
    },
    [state]
  )

  const markAllReadFull = useCallback(() => {
    const unread = (state.messages || []).filter(m => !m.is_read)
    if (!unread.length) {
      info("No unread messages in this folder.")
      return
    }
    // Select all then bulk-mark read (bulk acts on the current selection).
    state.selectAll()
    setTimeout(() => state.handleBulkAction("read"), 0)
    state.showToast(
      `Marking ${unread.length} message${
        unread.length === 1 ? "" : "s"
      } as read…`,
      "success"
    )
  }, [state, info])

  const readAloud = useCallback(() => {
    const m = active()
    if (!m) return
    if (!("speechSynthesis" in window)) {
      state.showToast("Read Aloud is not supported in this browser.", "error")
      return
    }
    const text = `${m.subject || ""}. ${(
      m.body_text ||
      m.body_html ||
      ""
    ).replace(/<[^>]+>/g, " ")}`
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 8000)
    setReadAloudText(text || m.subject || "No content")
  }, [state])

  const openMessageWindow = useCallback(
    (clean = false) => {
      const m = active()
      if (!m) return
      const w = window.open("", "_blank", "width=820,height=720")
      if (!w) return
      const esc = s => String(s || "").replace(/</g, "&lt;")
      const body =
        m.body_html ||
        `<pre style="white-space:pre-wrap;font-family:Segoe UI,Arial">${esc(
          m.body_text
        )}</pre>`
      const wrap = clean
        ? `max-width:680px;margin:0 auto;font-size:19px;line-height:1.7;`
        : `max-width:900px;margin:0 auto;`
      w.document.open()
      w.document.write(
        `<html><head><title>${
          esc(m.subject) || "Message"
        }</title></head><body style="font-family:'Segoe UI',Arial;padding:24px;color:#222;"><div style="${wrap}"><h2>${
          esc(m.subject) || "(no subject)"
        }</h2><p style="color:#666">From: ${esc(
          m.from_display || m.from_address
        )}</p><hr/>${body}</div></body></html>`
      )
      w.document.close()
    },
    [state]
  )

  const newFolder = useCallback(() => {
    setFolderModal({ mode: "create" })
  }, [])

  const renameFolder = useCallback(() => {
    const f = activeFolderObj()
    if (!f || !f.folder_id) {
      info("Pick a custom folder to rename (system folders can't be renamed).")
      return
    }
    setFolderModal({
      mode: "rename",
      folderId: f.folder_id,
      initialName: f.name,
    })
  }, [activeFolderObj, info])

  const saveFolderModal = useCallback(
    async name => {
      setFolderModalSaving(true)
      try {
        if (folderModal?.mode === "rename") {
          await emailApi.updateFolder(folderModal.folderId, { name })
          await state.fetchFolders(state.activeAccount?.account_id)
          state.selectFolder(name)
          await refreshFolderData(name)
          state.showToast("Folder renamed.", "success")
        } else {
          await emailApi.createFolder({
            name,
            account: state.activeAccount?.account_id,
          })
          await state.fetchFolders(state.activeAccount?.account_id)
          await refreshFolderData(state.activeFolder)
          state.showToast(`Folder "${name}" created.`, "success")
        }
        setFolderModal(null)
      } catch (e) {
        const detail = e?.response?.data
        const msg =
          detail && typeof detail === "object"
            ? Object.values(detail).flat().join(" ")
            : folderModal?.mode === "rename"
            ? "Could not rename the folder."
            : "Could not create the folder."
        state.showToast(msg, "error")
      } finally {
        setFolderModalSaving(false)
      }
    },
    [folderModal, state, refreshFolderData]
  )

  const deleteFolder = useCallback(async () => {
    const f = activeFolderObj()
    if (!f || !f.folder_id) {
      info("Pick a custom folder to delete (system folders can't be deleted).")
      return
    }
    if (
      !window.confirm(
        `Delete the folder "${f.name}"? Its messages are kept in the mailbox.`
      )
    )
      return
    try {
      await emailApi.deleteFolder(f.folder_id)
      await state.fetchFolders(state.activeAccount?.account_id)
      state.selectFolder("inbox")
      state.showToast("Folder deleted.", "success")
    } catch {
      state.showToast("Could not delete the folder.", "error")
    }
  }, [state, activeFolderObj, info])

  const deleteAllLoaded = useCallback(() => {
    const msgs = state.messages || []
    if (!msgs.length) {
      info("This folder is empty.")
      return
    }
    if (
      !window.confirm(
        `Delete all ${msgs.length} loaded message(s) in this folder?`
      )
    )
      return
    msgs.forEach(m => state.handleDelete(m))
    state.showToast(`Deleting ${msgs.length} message(s)…`, "success")
  }, [state, info])

  const sync = useCallback(() => {
    if (workOffline) {
      info("You are working offline. Turn off Work Offline to sync.")
      return
    }
    state.syncNow(state.activeAccount?.account_id)
  }, [state, workOffline, info])

  const resetView = useCallback(() => {
    setDensity("normal")
    setMessagePreview(true)
    setShowConversations(false)
    setSortBy("date")
    setSortDir("desc")
    setFolderPaneOpen(true)
    state.setShowReadingPane(true)
    info("View reset to defaults.")
  }, [state, info])

  const diagnostics = useCallback(() => {
    state.showToast(
      `Diagnostics — Account: ${
        state.activeAccount?.email_address || "—"
      } · Folder: ${state.activeFolder} · Items: ${
        state.messagesTotal || 0
      } · Unread: ${state.stats?.unread || 0}`,
      "info"
    )
  }, [state])

  const ribbonHandlers = {
    // Account
    viewAccount: () => {
      setAddingMailbox(false)
      setShowAccountSettings(true)
    },
    addAccount: () => {
      setAddingMailbox(true)
      setShowAccountSettings(true)
    },
    // New
    newMail: handleNewMail,
    // Delete group
    ignore: () => {
      const m = active()
      if (m) {
        state.handleArchive(m)
        state.setActiveMessage(null)
      }
    },
    cleanUp: markAllReadFull,
    junk: () => moveActiveTo("junk"),
    delete: () => {
      const m = active()
      if (m) state.handleDelete(m)
    },
    archive: () => {
      const m = active()
      if (m) state.handleArchive(m)
    },
    // Respond
    reply: () => {
      const m = active()
      if (m) handleReply(m)
    },
    replyAll: () => {
      const m = active()
      if (m) handleReplyAll(m)
    },
    forward: () => {
      const m = active()
      if (m) handleForward(m)
    },
    meeting: () => setShowMeeting(true),
    more: e => {
      const m = active()
      if (m) state.handleContextMenu(e, m)
    },
    // Quick steps
    qsReminder: handleNewMail,
    qsTeam: handleNewMail,
    qsReplyDelete: () => {
      const m = active()
      if (m) {
        handleReply(m)
        state.handleDelete(m)
      }
    },
    qsToManager: () => {
      const m = active()
      if (m) handleForward(m)
    },
    qsDone: () => {
      const m = active()
      if (m) {
        state.handleArchive(m)
        state.setActiveMessage(null)
      }
    },
    qsCreate: handleNewMail,
    // Move / Rules
    move: () => moveActiveTo("archive"),
    rules: () => setShowRules(true),
    oneNote: () => {
      const m = active()
      if (m) handleDownload(m)
    },
    // Tags
    unreadRead: () => {
      if (state.selectedMessages && state.selectedMessages.size > 0)
        state.handleBulkAction("read")
      else {
        const m = active()
        if (m) {
          state.handleToggleStar(m)
        }
        info("Select messages to mark read/unread.")
      }
    },
    categorise: () => {
      const m = active()
      if (m) state.handleToggleStar(m)
    },
    followUp: () => {
      const m = active()
      if (m) state.handleToggleFlag(m)
    },
    // Find
    searchPeople: () => state.setSearchOpen(true),
    addressBook: () => setActiveView("people"),
    filterEmail: () =>
      info('Use the "All / Unread" tabs above the message list to filter.'),
    // Speech / language / add-ins
    readAloud,
    translate: () => {
      const m = active()
      if (!m) return
      const text = (m.body_text || m.body_html || "")
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .slice(0, 1500)
      window.open(
        `https://translate.google.com/?sl=auto&tl=en&text=${encodeURIComponent(
          text
        )}&op=translate`,
        "_blank"
      )
    },
    getAddins: () => info("No add-ins are installed for this mailbox."),
    // Send / Receive
    srAll: sync,
    updateFolder: sync,
    sendAll: sync,
    srGroups: sync,
    showProgress: () =>
      info(
        state.folderSyncing
          ? "A sync is currently in progress…"
          : "No send/receive is in progress."
      ),
    cancelAll: () => info("There is no active send/receive to cancel."),
    downloadHeaders: sync,
    markDownload: () =>
      info("Header-only download applies to POP/IMAP mailboxes."),
    unmarkDownload: () =>
      info("Header-only download applies to POP/IMAP mailboxes."),
    processHeaders: sync,
    downloadPrefs: () => {
      setAddingMailbox(false)
      setShowAccountSettings(true)
    },
    workOffline: () =>
      setWorkOffline(v => {
        const nv = !v
        state.showToast(
          nv ? "Working offline — syncing paused." : "Back online.",
          "info"
        )
        return nv
      }),
    // Folder
    newFolder,
    newSearchFolder: () => state.setSearchOpen(true),
    renameFolder,
    copyFolder: () => info("Copying folders isn't supported on this server."),
    moveFolder: () => info("Moving folders isn't supported on this server."),
    deleteFolder,
    markAllRead: markAllReadFull,
    runRules: applyRules,
    cleanUpFolder: markAllReadFull,
    deleteAll: deleteAllLoaded,
    recoverDeleted: () => state.selectFolder("trash"),
    addFavourites: () =>
      info("The Inbox and Starred are already in Favourites."),
    viewOnServer: () => openMessageWindow(false),
    autoArchive: () => {
      setAddingMailbox(false)
      setShowAccountSettings(true)
    },
    folderPermissions: () =>
      info("Folder permissions are managed on the mail server."),
    folderProperties: () =>
      info(
        `Folder: ${state.activeFolder} · ${state.messagesTotal || 0} item(s).`
      ),
    // View
    changeView: () => setDensity(d => (d === "compact" ? "normal" : "compact")),
    viewSettings: () => {
      setAddingMailbox(false)
      setShowAccountSettings(true)
    },
    resetView,
    showConversations: () => setShowConversations(v => !v),
    convSettings: () =>
      info("Conversation grouping groups messages with the same subject."),
    messagePreview: () => setMessagePreview(v => !v),
    sortDate: () => setSortBy("date"),
    sortFlag: () => setSortBy("flag"),
    sortFrom: () => setSortBy("from"),
    reverseSort: () => setSortDir(d => (d === "desc" ? "asc" : "desc")),
    addColumns: () =>
      info("Columns: Sender, Subject, Received and Flag are shown."),
    expandCollapse: () => info("Message groups expand and collapse by date."),
    tighterSpacing: () =>
      setDensity(d => (d === "compact" ? "normal" : "compact")),
    folderPane: () => setFolderPaneOpen(v => !v),
    readingPane: () => state.setShowReadingPane(v => !v),
    todoBar: () => setActiveView("tasks"),
    remindersWindow: () => setShowReminders(true),
    openNewWindow: () => openMessageWindow(false),
    closeAll: () => state.setActiveMessage(null),
    immersiveReader: () => openMessageWindow(true),
    // Help
    help: () =>
      info(
        "BharatATS Mail — use the ribbon tabs (Home, Send/Receive, Folder, View) to work with your mail."
      ),
    feedback: handleNewMail,
    suggest: handleNewMail,
    training: () => info("Hover any ribbon button to see what it does."),
    support: () => {
      setAddingMailbox(false)
      setShowAccountSettings(true)
    },
    diagnostics,
  }

  const acctEmail = state.activeAccount?.email_address || ""
  const acctName = state.activeAccount?.display_name || acctEmail || "Account"
  const acctInitial =
    (acctName || acctEmail || "U").trim().charAt(0).toUpperCase() || "U"

  const ribbonUi = {
    hasMessage: !!state.activeMessage,
    account: { name: acctName, email: acctEmail, initial: acctInitial },
    workOffline,
    showConversations,
    messagePreview,
    density,
    sortBy,
    folderPaneOpen,
    readingPane: state.showReadingPane,
  }

  const VIEW_TITLES = {
    mail: "Mail",
    calendar: "Calendar",
    people: "People",
    tasks: "To Do",
  }

  return (
    <div className={`em-module ${ribbonCollapsed ? "em-ribbon-collapsed" : ""}`}>
      {/* Outlook-style top ribbon with tabbed toolbars (Home / Send·Receive / View). */}
      <EmailRibbon
        h={ribbonHandlers}
        ui={ribbonUi}
        collapsed={ribbonCollapsed}
        onToggleCollapsed={() => setRibbonCollapsed(value => !value)}
      />

      <div className="em-module-body">
        {activeView === "mail" && (
          <>
            {folderPaneOpen ? (
              <EmailSidebar
                accounts={state.accounts}
                activeAccount={state.activeAccount}
                onSelectAccount={state.setActiveAccount}
                folders={state.folders}
                activeFolder={state.activeFolder}
                onSelectFolder={state.selectFolder}
                stats={state.stats}
                labels={state.labels}
                onOpenCompose={handleNewMail}
                onOpenSettings={() => {
                  setAddingMailbox(false)
                  setShowAccountSettings(true)
                }}
                onAddMailbox={() => {
                  setAddingMailbox(true)
                  setShowAccountSettings(true)
                }}
                onSync={() => state.syncNow()}
                onOpenSearch={() => state.setSearchOpen(true)}
                onCollapse={() => setFolderPaneOpen(false)}
                onFolderContextAction={handleFolderContextAction}
              />
            ) : (
              <div className="em-collapsed-rail">
                <button
                  className="em-collapsed-expand"
                  title="Expand folder pane"
                  onClick={() => setFolderPaneOpen(true)}
                  type="button"
                >
                  <Icons.ChevronsRight size={16} />
                </button>
              </div>
            )}

            <EmailList
              messages={state.messages}
              loading={state.loading}
              loadingMore={state.loadingMore}
              activeMessage={state.activeMessage}
              selectedMessages={state.selectedMessages}
              onSelect={state.toggleSelect}
              onSelectAll={state.selectAll}
              onMessageClick={state.selectMessage}
              onToggleStar={state.handleToggleStar}
              onToggleFlag={state.handleToggleFlag}
              onContextMenu={state.handleContextMenu}
              onLoadMore={state.loadMore}
              activeFolder={state.activeFolder}
              folderSyncing={state.folderSyncing}
              density={density}
              showPreview={messagePreview}
              showConversations={showConversations}
              sortBy={sortBy}
              sortDir={sortDir}
              page={state.messagesPage}
              pageSize={state.messagesPageSize}
              total={state.messagesTotal}
              onNextPage={() => state.goToPage(state.messagesPage + 1)}
              onPrevPage={() => state.goToPage(state.messagesPage - 1)}
            />

            {state.showReadingPane && (
              <EmailViewer
                message={state.activeMessage}
                onClose={() => state.setActiveMessage(null)}
                onReply={handleReply}
                onReplyAll={handleReplyAll}
                onForward={handleForward}
                onDelete={state.handleDelete}
                onArchive={state.handleArchive}
                onToggleStar={state.handleToggleStar}
                onToggleFlag={state.handleToggleFlag}
                onPrint={handlePrint}
                onDownload={handleDownload}
                showToast={state.showToast}
                onMarkUnread={markActiveUnread}
                onJunk={() => moveActiveTo("junk")}
              />
            )}
          </>
        )}

        {activeView === "calendar" && (
          <EmailCalendar
            activeAccount={state.activeAccount}
            showToast={state.showToast}
          />
        )}
        {activeView === "people" && (
          <EmailPeople
            activeAccount={state.activeAccount}
            onComposeTo={handleComposeTo}
          />
        )}
        {activeView === "tasks" && <EmailTasks />}
      </div>

      {/* Bottom bar: Outlook-style module switcher on the left + status on the right. */}
      <div className="em-statusbar">
        <EmailNavRail
          activeView={activeView}
          onChangeView={setActiveView}
          unread={state.stats?.unread || 0}
          onMore={() =>
            info("More apps: Mail, Calendar, People and To Do are available.")
          }
        />
        <div className="em-statusbar-info">
          <span>Items: {state.messagesTotal || 0}</span>
          <span>Unread: {state.stats?.unread || 0}</span>
          <span className="em-statusbar-conn">
            Connected to: Microsoft Exchange
          </span>
        </div>
      </div>

      {showCompose && (
        <EmailCompose
          smtpAccounts={smtpAccounts}
          activeAccount={state.activeAccount}
          mode={composeMode}
          initial={composeInitial}
          onClose={handleComposeClose}
          onSent={handleComposeSent}
          showToast={state.showToast}
        />
      )}

      {state.searchOpen && (
        <EmailSearch
          onSearch={state.handleSearch}
          onClose={() => state.setSearchOpen(false)}
        />
      )}

      {state.contextMenu && (
        <EmailContextMenu
          x={state.contextMenu.x}
          y={state.contextMenu.y}
          message={state.contextMenu.message}
          folders={state.folders}
          onClose={state.closeContextMenu}
          onReply={handleReply}
          onReplyAll={handleReplyAll}
          onForward={handleForward}
          onMarkRead={msg => state.handleBulkAction("read")}
          onMarkUnread={msg => state.handleBulkAction("unread")}
          onStar={state.handleToggleStar}
          onFlag={state.handleToggleFlag}
          onMove={state.handleMove}
          onDelete={state.handleDelete}
          onArchive={state.handleArchive}
        />
      )}

      {showAccountSettings && (
        <EmailAccountSettings
          account={addingMailbox ? null : state.activeAccount}
          isAdditional={addingMailbox && state.accounts?.length > 0}
          onClose={() => {
            setShowAccountSettings(false)
            setAddingMailbox(false)
          }}
          onSaved={acct => {
            setShowAccountSettings(false)
            setAddingMailbox(false)
            state.applyAccount(acct)
            state.syncNow(acct.account_id)
          }}
          onLoggedOut={accountId => {
            setShowAccountSettings(false)
            setAddingMailbox(false)
            state.removeAccount(accountId)
          }}
          showToast={state.showToast}
        />
      )}

      {showRules && (
        <EmailRules
          rules={rules}
          onSave={saveRules}
          onRunRules={list => {
            applyRules(list)
          }}
          onClose={() => setShowRules(false)}
        />
      )}

      {folderModal && (
        <EmailFolderModal
          mode={folderModal.mode}
          initialName={folderModal.initialName}
          saving={folderModalSaving}
          onSave={saveFolderModal}
          onClose={() => setFolderModal(null)}
        />
      )}

      {folderProperties && (
        <div className="em-search-overlay" onClick={() => setFolderProperties(null)}>
          <div className="em-cal-modal em-folder-properties" onClick={e => e.stopPropagation()}>
            <div className="em-cal-modal-header">
              <h3>{folderProperties.folder?.label || folderProperties.folder?.name} Properties</h3>
              <button className="em-search-close" onClick={() => setFolderProperties(null)}>
                <Icons.X size={18} />
              </button>
            </div>
            <div className="em-cal-modal-body">
              {Object.entries(folderProperties.data || {}).map(([key, value]) => (
                <div className="em-folder-property-row" key={key}>
                  <span>{key.replaceAll("_", " ")}</span>
                  <strong>{typeof value === "object" ? JSON.stringify(value) : String(value ?? "—")}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {state.newMailNotice && (
        <EmailNewMailToast
          message={state.newMailNotice.msg}
          count={state.newMailNotice.count}
          onOpen={() => {
            state.selectMessage(state.newMailNotice.msg)
            state.setNewMailNotice(null)
          }}
          onDelete={() => {
            state.handleDelete(state.newMailNotice.msg)
            state.setNewMailNotice(null)
          }}
          onFlag={() => {
            state.handleToggleFlag(state.newMailNotice.msg)
            state.setNewMailNotice(null)
          }}
          onClose={() => state.setNewMailNotice(null)}
        />
      )}

      {readAloudText && (
        <EmailReadAloud
          text={readAloudText}
          onClose={() => setReadAloudText(null)}
        />
      )}

      {showMeeting && (
        <EmailMeeting
          onClose={() => setShowMeeting(false)}
          defaultAttendees={state.activeMessage?.from_address || ""}
          showToast={state.showToast}
        />
      )}

      {showReminders && (
        <EmailReminders
          onClose={() => setShowReminders(false)}
          accountId={state.activeAccount?.account_id}
          flagged={(state.messages || []).filter(m => m.is_flagged)}
          onOpenMessage={state.selectMessage}
        />
      )}

      {state.toast && (
        <div className={`em-toast em-toast-${state.toast.type}`}>
          {state.toast.type === "error" ? (
            <Icons.XCircle size={16} />
          ) : state.toast.type === "warning" ? (
            <Icons.AlertTriangle size={16} />
          ) : state.toast.type === "info" ? (
            <Icons.Info size={16} />
          ) : (
            <Icons.CheckCircle size={16} />
          )}
          <span>{state.toast.message}</span>
        </div>
      )}
    </div>
  )
}
